// System colors (white/black) are NAV masterdata rows whose names arrive in a
// fixed language (id: "HVID"/"SORT", name: "Hvid"/"Sort", nickname:
// "White"/"Black"). Per decision 11/7 they must (1) DISPLAY in the user's
// language and (2) be FINDABLE in color searches by any of the 10 supported
// languages' words for white/black — narrowed per decision 11/7 (2nd round):
// only the ACTIVE UI language's word plus English matches, so e.g. "blanc"
// finds white on French UI but not on Polish. All other color names
// (Pantone/special) stay raw and are matched exactly as before.
//
// Identification follows the precedent in core/models/entities/BaseLogo.ts
// (getColorKey): the stable NAV id "HVID"/"SORT" is the primary key. The NAV
// name "Hvid"/"Sort" is kept as a fallback because existing special cases
// (ColorBlockWithToolTip, VectorColorElement) matched on name and the
// embroidery (Polyneon/Madeira) catalog may use different ids.

import i18next from "../i18n";

// Minimal shape so the helpers work for both Color (LogoOptions) and ColorDto.
export interface SystemColorLike {
  id?: string | null;
  name?: string | null;
  nickname?: string | null;
  isBackup?: boolean | null;
}

// t from useTranslation / i18next.t both satisfy this.
type Translate = (key: string) => string;

// The catalogs are inconsistent about how the system colors are identified:
// the logo detail data uses id "HVID"/"SORT" with Danish names, while the
// change-color catalog carries them with English identity (the dropdown
// literally shows "White"/"Black" as nickname||name). Detect by EXACT match
// on any of id/name/nickname against the known identities — exact equality,
// never substring, so Pantone names like "Off White" or "Arsenal Third
// Blue" can never be misdetected.
function matchesIdentity(
  color: SystemColorLike,
  identities: ReadonlyArray<string>,
): boolean {
  return [color.id, color.name, color.nickname].some(
    (v) => !!v && identities.includes(v.trim().toLowerCase()),
  );
}

export function isSystemWhite(color: SystemColorLike): boolean {
  return matchesIdentity(color, ["hvid", "white"]);
}

export function isSystemBlack(color: SystemColorLike): boolean {
  return matchesIdentity(color, ["sort", "black"]);
}

// Per-language alias sets: the word for white/black in each of the 10
// supported UI languages. Spellings with diacritics that do NOT decompose via
// NFD (Polish l-stroke, German sharp s) are listed in both forms so a
// plain-ASCII query matches too (bialy AND biały, weiss AND weiß). Search
// only consults the ACTIVE language's entry plus English.
const WHITE_ALIASES_BY_LANG: Record<string, ReadonlyArray<string>> = {
  en: ["white"],
  da: ["hvid"],
  de: ["weiss", "weiß"],
  es: ["blanco"],
  fr: ["blanc"],
  it: ["bianco"],
  nl: ["wit"],
  no: ["hvit"],
  pl: ["biały", "bialy"],
  sv: ["vit"],
};

const BLACK_ALIASES_BY_LANG: Record<string, ReadonlyArray<string>> = {
  en: ["black"],
  da: ["sort"],
  de: ["schwarz"],
  es: ["negro"],
  fr: ["noir"],
  it: ["nero"],
  nl: ["zwart"],
  no: ["svart"],
  pl: ["czarny"],
  sv: ["svart"],
};

// Lowercase + strip diacritics. NFD handles combining marks (é, å, ö, ...);
// the explicit replacements cover letters that are NOT decomposable (ł, ß).
function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/ß/g, "ss");
}

// Resolved lazily so a language switch takes effect on the next keystroke —
// MUI's filter (and its stringify) is created once at module level.
function activeLanguageAliases(
  map: Record<string, ReadonlyArray<string>>,
): string[] {
  const base = (i18next.language || "en").toLowerCase().split("-")[0];
  return Array.from(new Set([...(map[base] ?? []), ...map.en]));
}

/**
 * Translated display name for the system colors, null for everything else.
 * Callers keep their own fallback so existing raw-name behavior for
 * Pantone/special colors is untouched:
 *   systemColorDisplayName(color, t) ?? color.name              // tooltips
 *   systemColorDisplayName(color, t) ?? (color.nickname || color.name) // pickers
 * Backup White keeps its dedicated label (do not change its logic elsewhere).
 */
export function systemColorDisplayName(
  color: SystemColorLike,
  t: Translate,
): string | null {
  // Backup rows FIRST, regardless of identity: the logo system delivers the
  // white-backup layer as a real Pantone row (customer case 21/8: code "0821",
  // the industry's pale-blue stand-in for white backup foil), so name-based
  // white detection never matches it — and customers then see a mystery
  // "PMS 0821 C" instead of learning their logo HAS white backup. The flag is
  // authoritative (IsBackground in the DB, verified correct).
  if (color.isBackup) {
    return t("logoEditorComponents.backupWhite");
  }
  if (isSystemWhite(color)) {
    return t("logoEditorComponents.colorWhite");
  }
  if (isSystemBlack(color)) {
    return t("logoEditorComponents.colorBlack");
  }
  return null;
}

/** Picker/dropdown label: translated for white/black, raw nickname||name otherwise. */
export function colorPickerLabel(color: SystemColorLike, t: Translate): string {
  return (
    systemColorDisplayName(color, t) ?? (color.nickname || color.name || "")
  );
}

/**
 * Search text for MUI Autocomplete's createFilterOptions({ stringify }).
 * For white/black: the raw label (nickname||name — what matched before) plus
 * the ACTIVE UI language's word and the English word, so "biały" finds white
 * on Polish UI and "white" always works, but other languages' words do not
 * bleed into the results. For every other color: exactly the label MUI
 * matched on before, so Pantone search behavior is unchanged.
 */
export function colorSearchText(color: SystemColorLike): string {
  const label = color.nickname || color.name || "";
  if (isSystemWhite(color)) {
    return [label, ...activeLanguageAliases(WHITE_ALIASES_BY_LANG)].join(" ");
  }
  if (isSystemBlack(color)) {
    return [label, ...activeLanguageAliases(BLACK_ALIASES_BY_LANG)].join(" ");
  }
  return label;
}

/**
 * Standalone query match (for non-MUI search UIs). Case-insensitive and
 * diacritic-tolerant for the white/black aliases; other colors match their
 * raw nickname||name case-insensitively exactly as today.
 */
export function colorMatchesQuery(
  color: SystemColorLike,
  query: string,
): boolean {
  const raw = (color.nickname || color.name || "").toLowerCase();
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  if (raw.includes(q)) return true;

  const aliasMap = isSystemWhite(color)
    ? WHITE_ALIASES_BY_LANG
    : isSystemBlack(color)
      ? BLACK_ALIASES_BY_LANG
      : null;
  if (!aliasMap) return false;

  const qNorm = normalizeForSearch(q);
  return activeLanguageAliases(aliasMap).some((alias) =>
    normalizeForSearch(alias).includes(qNorm),
  );
}
